import React from 'react';
import { StatoContenuto } from '../types';

interface StatusBadgeProps {
  stato: StatoContenuto;
  size?: 'xs' | 'sm' | 'md' | 'lg';
  showDot?: boolean;
  compact?: boolean; // Per mostrare abbreviazioni su mobile
  className?: string;
}

const StatusBadge: React.FC<StatusBadgeProps> = ({
  stato,
  size = 'md',
  showDot = false,
  compact = false,
  className = ''
}) => {
  const getStatusConfig = (stato: StatoContenuto) => {
    switch (stato) {
      case StatoContenuto.BOZZA:
        return {
          label: 'Bozza',
          labelShort: 'Bozza',
          bg: 'bg-slate-100 dark:bg-slate-800',
          text: 'text-slate-700 dark:text-slate-300',
          dot: 'bg-slate-400',
          border: 'border-slate-200 dark:border-slate-700'
        };
      case StatoContenuto.TESTO_PRONTO:
        return {
          label: 'Testo confermato',
          labelShort: 'Testo',
          bg: 'bg-emerald-50 dark:bg-emerald-900/30',
          text: 'text-emerald-700 dark:text-emerald-300',
          dot: 'bg-emerald-500',
          border: 'border-emerald-200 dark:border-emerald-800'
        };
      case StatoContenuto.REGISTRATO:
        return {
          label: 'Registrato',
          labelShort: 'Reg.',
          bg: 'bg-amber-50 dark:bg-amber-900/30',
          text: 'text-amber-700 dark:text-amber-300',
          dot: 'bg-amber-500',
          border: 'border-amber-200 dark:border-amber-800'
        };
      case StatoContenuto.MONTATO:
        return {
          label: 'Montato',
          labelShort: 'Mont.',
          bg: 'bg-purple-50 dark:bg-purple-900/30',
          text: 'text-purple-700 dark:text-purple-300',
          dot: 'bg-purple-500',
          border: 'border-purple-200 dark:border-purple-800'
        };
      case StatoContenuto.PROGRAMMATO:
        return {
          label: 'Programmato',
          labelShort: 'Prog.',
          bg: 'bg-orange-50 dark:bg-orange-900/30',
          text: 'text-orange-700 dark:text-orange-300',
          dot: 'bg-orange-500',
          border: 'border-orange-200 dark:border-orange-800'
        };
      case StatoContenuto.PUBBLICATO:
        return {
          label: 'Pubblicato',
          labelShort: 'Pubbl.',
          bg: 'bg-green-50 dark:bg-green-900/30',
          text: 'text-green-700 dark:text-green-300',
          dot: 'bg-green-500',
          border: 'border-green-200 dark:border-green-800'
        };
      case StatoContenuto.ARCHIVIATO:
        return {
          label: 'Archiviato',
          labelShort: 'Arch.',
          bg: 'bg-red-50 dark:bg-red-900/30',
          text: 'text-red-700 dark:text-red-300',
          dot: 'bg-red-400',
          border: 'border-red-200 dark:border-red-800'
        };
      default:
        return {
          label: 'Sconosciuto',
          labelShort: '?',
          bg: 'bg-slate-100 dark:bg-slate-800',
          text: 'text-slate-700 dark:text-slate-300',
          dot: 'bg-slate-400',
          border: 'border-slate-200 dark:border-slate-700'
        };
    }
  };

  // Dimensioni responsive e touch-friendly
  const sizeStyles = {
    xs: 'px-1.5 py-0.5 text-[9px] sm:text-[10px]',
    sm: 'px-2 py-0.5 text-[10px] sm:text-[11px]',
    md: 'px-2 sm:px-2.5 py-0.5 sm:py-1 text-[11px] sm:text-xs',
    lg: 'px-2.5 sm:px-3 py-1 sm:py-1.5 text-xs sm:text-sm'
  };

  const dotSizes = {
    xs: 'w-1 h-1 sm:w-1.5 sm:h-1.5',
    sm: 'w-1.5 h-1.5',
    md: 'w-1.5 h-1.5 sm:w-2 sm:h-2',
    lg: 'w-2 h-2 sm:w-2.5 sm:h-2.5'
  };

  const config = getStatusConfig(stato);

  return (
    <span
      className={`
        inline-flex items-center gap-1 sm:gap-1.5
        ${sizeStyles[size]}
        ${config.bg} ${config.text}
        border ${config.border}
        rounded-full font-medium
        transition-all duration-200
        whitespace-nowrap
        select-none
        ${className}
      `}
      title={config.label}
    >
      {showDot && (
        <span className={`${dotSizes[size]} ${config.dot} rounded-full flex-shrink-0`} />
      )}
      {/* Mostra label corta su mobile se compact e' true */}
      {compact ? (
        <>
          <span className="sm:hidden">{config.labelShort}</span>
          <span className="hidden sm:inline">{config.label}</span>
        </>
      ) : (
        config.label
      )}
    </span>
  );
};

// Componente wrapper per lista di badge responsive
export const StatusBadgeList: React.FC<{
  stati: StatoContenuto[];
  size?: 'xs' | 'sm' | 'md' | 'lg';
  maxVisible?: number;
}> = ({ stati, size = 'sm', maxVisible = 3 }) => {
  const visibleStati = stati.slice(0, maxVisible);
  const hiddenCount = stati.length - maxVisible;

  return (
    <div className="flex flex-wrap gap-1 sm:gap-1.5">
      {visibleStati.map((stato, index) => (
        <StatusBadge key={index} stato={stato} size={size} compact />
      ))}
      {hiddenCount > 0 && (
        <span className={`
          inline-flex items-center justify-center
          ${size === 'xs' ? 'text-[9px] px-1' : size === 'sm' ? 'text-[10px] px-1.5' : 'text-xs px-2'}
          py-0.5 bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 rounded-full
        `}>
          +{hiddenCount}
        </span>
      )}
    </div>
  );
};

export default StatusBadge;
